// src/components/safety/editor/ChecklistEditor.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import LinkDocSelector from "../LinkDocSelector";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from "@dnd-kit/sortable";

import Modal from "../../common/ui/Modal";
import TableCell from "../../common/ui/TableCell";
import SortablePhoto from "../../common/viewer/SortablePhoto";
import ReferenceModal from "../../approval/ReferenceModal";
import MobileReferenceModal from "../../approval/MobileReferenceModal";
import ChecklistResultSection from "./ChecklistResultSection";
import TinyMCEeditor from "../../const/TinyMCEeditor";
import { useIsMobile } from "../../../api/useIsMobile";

const DOC_TYPE_OPTIONS = [{ value: "check", label: "점검 예정" }];

export default function ChecklistEditor({
  initial = {},
  onChange,
  readOnly = false,
  projectId,
}) {
  const today = new Date().toISOString().slice(0, 10);
  const editorRef = useRef(null);
  const isMobile = useIsMobile();

  const [photos, setPhotos] = useState([]);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const STATUS_BY_TYPE = {
    check: [
      { value: "planned", label: "점검 예정" },
      { value: "action_needed", label: "조치 필요" },
      { value: "in_progress", label: "조치 중" },
      { value: "completed", label: "조치 완료" },
      { value: "failed", label: "부적합" },
      { value: "urgent", label: "긴급" },
      { value: "cancelled", label: "취소" },
    ],
  };
  const [docType, setDocType] = useState(
    (initial.doc_type || initial.docType || "check").toLowerCase()
  );

  const defaultStatus = useMemo(() => {
    if (initial.status) return initial.status;
    if (initial.docType === "result") return "completed";
    return "planned"; // check 기본값
  }, [initial]);

  const [progress, setProgress] = useState(defaultStatus || "planned");

  useEffect(() => {
    if (docType === "result") {
      // 결과문서는 기본적으로 결과작성 모드 활성화
      setShowResultInput(true);
    } else {
      // check 은 처음엔 비활성화
      setShowResultInput(false);
    }
  }, [docType]);

  useEffect(() => {
    const options = STATUS_BY_TYPE[docType] || [];
    const validValues = options.map((o) => o.value);
    if (!validValues.includes(progress)) {
      setProgress(validValues[0] || "planned");
    }
  }, [docType, progress]);

  const [title, setTitle] = useState(initial.title || "");
  const [checkType, setCheckType] = useState(initial.check_type || "공종별");
  const [memo, setMemo] = useState(initial.memo || "");
  const [inspector, setInspector] = useState(initial.inspector || "");
  const [confirmer, setConfirmer] = useState(initial.confirmer || "");
  const [attachment, setAttachment] = useState(initial.attachment || null);

  const safeInitDate =
    typeof initial.check_date === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(initial.check_date)
      ? initial.check_date
      : ""; // 빈값 유지

  const [checkDate, setCheckDate] = useState(safeInitDate);
  const [overallResult, setOverallResult] = useState(
    initial.overall_result || "미조치"
  );

  const [showResultInput, setShowResultInput] = useState(false);

  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkedDoc, setLinkedDoc] = useState(initial.linkedDoc || null);

  const [rows, setRows] = useState(
    Array.isArray(initial.items) && initial.items.length > 0
      ? initial.items.map((r) => ({ id: crypto.randomUUID(), ...r }))
      : [
          {
            id: crypto.randomUUID(),
            category: "",
            item: "",
            criteria: "",
            result: "NA",
            remark: "",
          },
        ]
  );

  const [resultStatus, setResultStatus] = useState("in_progress");
  const [failReason, setFailReason] = useState("");
  const [failOwner, setFailOwner] = useState("");

  useEffect(() => {
    if (!initial) return;

    // 문서 유형 (check / result)
    const initType = String(initial.doc_type || initial.docType || "")
      .trim()
      .toLowerCase();
    if (["check", "result"].includes(initType)) {
      setDocType(initType);
    }

    // 진행 상태
    if (initial.progress) setProgress(initial.progress);

    // 점검 항목 구분
    if (initial.check_type) setCheckType(initial.check_type);

    // 전체 결과
    if (initial.overall_result) setOverallResult(initial.overall_result);

    // 점검일
    if (
      typeof initial.check_date === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(initial.check_date)
    ) {
      setCheckDate(initial.check_date);
    } else {
      setCheckDate(""); // 잘못된 값은 제거
    }

    // 제목, 메모
    if (initial.title) setTitle(initial.title);
    if (initial.memo) setMemo(initial.memo);
    if (initial.inspector) setInspector(initial.inspector);
    if (initial.confirmer) setConfirmer(initial.confirmer);

    // 점검 항목 데이터 (id 유실 방지)
    if (Array.isArray(initial.items)) {
      setRows(
        initial.items.length > 0
          ? initial.items.map((r) => ({ id: crypto.randomUUID(), ...r }))
          : [
              {
                id: crypto.randomUUID(),
                category: "",
                item: "",
                criteria: "",
                result: "NA",
                remark: "",
              },
            ]
      );
    }
  }, [
    initial?.doc_type,
    initial?.docType,
    initial?.progress,
    initial?.check_type,
    initial?.overall_result,
    initial?.check_date,
    initial?.title,
    initial?.memo,
    initial?.items,
  ]);

  const isDone = progress === "completed";
  const isFail = progress === "failed" || progress === "urgent";

  const progressLabel = useMemo(() => {
    const option = (STATUS_BY_TYPE[docType] || []).find(
      (opt) => opt.value === progress
    );
    return option?.label || "미정";
  }, [docType, progress]);

  // 자동 제목
  const autoTitle = useMemo(() => {
    return `[안전 점검] ${checkType} - ${checkDate} (${progressLabel})`;
  }, [checkType, checkDate, progressLabel]);

  // 결과 요약
  const summary = useMemo(() => {
    const pass = rows.filter((r) => r.result === "PASS").length;
    const fail = rows.filter((r) => r.result === "FAIL").length;
    const na = rows.filter((r) => r.result === "NA").length;
    return { total: rows.length, pass, fail, na };
  }, [rows]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAttachment(f);
    if (!title) setTitle(f.name);
  };

  const onPickPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setPhotos((prev) => [
      ...prev,
      ...files.map((f) => ({
        id: crypto.randomUUID(),
        file: f,
        name: f.name,
        size: f.size,
        type: f.type,
      })),
    ]);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setPhotos((prev) => {
        const oldIndex = prev.findIndex((p) => p.id === active.id);
        const newIndex = prev.findIndex((p) => p.id === over?.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  // --- ReferenceModal 상태 및 핸들러 ---
  const me = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);
  const [referenceUserIds, setReferenceUserIds] = useState([]); // 현재 선택된 ID들
  const [referenceUserDetails, setReferenceUserDetails] = useState([]); // 화면 표시용

  const [approvalLines, setApprovalLines] = useState([]); // 제외할 사람들
  const [viewMode, setViewMode] = useState("ALL"); // ALL=전체보기, REF=참조자보기, ME=나만보기

  // ReferenceModal 열기/닫기
  const [allUsers, setAllUsers] = useState([]);
  const openReferenceModal = () => setIsReferenceModalOpen(true);
  const closeReferenceModal = () => setIsReferenceModalOpen(false);

  // 선택 변경 핸들러
  const handleReferenceChange = (ids) => setReferenceUserIds(ids);

  // 저장 시 반영
  const handleReferenceSave = (ids, usersForFront = []) => {
    setReferenceUserIds(ids);

    setReferenceUserDetails(
      (usersForFront || []).filter((u) => u.id !== me.id)
    );

    return true; // ReferenceModal에서 ok !== false 체크하므로 true 반환
  };

  // console.log("참조자 선택 변경:", referenceUserIds);

  // 진행상태 → 전체결과 자동 동기화
  useEffect(() => {
    if (isDone) setOverallResult("완료");
    else if (isFail) setOverallResult("불합격");
    else setOverallResult("미조치");
  }, [progress, isDone, isFail]);

  // 부모로 전달
  useEffect(() => {
    if (!onChange) return;

    const meta = {
      progress,
      doc_date: checkDate,
      check_type: checkType,
      check_date: checkDate,
      inspector,
      confirmer,
      memo,
      items: rows.map(({ id, ...rest }) => rest),
      reference_users:
        viewMode === "ALL"
          ? "ALL"
          : viewMode === "ME"
          ? [me.id]
          : [...referenceUserIds, me.id],
      view_mode: viewMode,
    };

    onChange({
      dns: initial.dns || "",
      title: title?.trim() || autoTitle,
      doc_type: docType,
      status: "none",
      meta,
    });
  }, [
    docType,
    progress,
    title,
    memo,
    inspector,
    confirmer,
    checkType,
    checkDate,
    rows,
    autoTitle,
    onChange,
    linkedDoc,
    viewMode,
    referenceUserIds,
    me,
  ]);

  const addRow = () =>
    setRows((p) => [
      ...p,
      {
        id: crypto.randomUUID(),
        category: "",
        item: "",
        criteria: "",
        result: "NA",
        remark: "",
      },
    ]);

  const section = "";

  return (
    <div className="space-y-4 px-2 py-4">
      {/* ====================== 공통 정보 (모바일 최적화) ====================== */}
      <section className="space-y-3">
        {/* 제목 */}
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            제목
          </label>
          {!readOnly ? (
            <div>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={autoTitle}
              />
              <p className="mt-1 text-xs text-gray-500">
                미입력 시 자동 제목: {autoTitle}
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-900">{title || autoTitle}</p>
          )}
        </div>

        {/* 점검 상태 */}
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            점검 상태
          </label>
          {!readOnly ? (
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={progress}
              onChange={(e) => setProgress(e.target.value)}
            >
              {(STATUS_BY_TYPE[docType] || []).map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-gray-900">{progressLabel}</p>
          )}
        </div>

        {/* 점검일 */}
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            {docType === "check"
              ? "점검 예정일"
              : docType === "result"
              ? "점검 완료일"
              : "점검일"}
          </label>
          {!readOnly ? (
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={checkDate}
              onChange={(e) => setCheckDate(e.target.value)}
            />
          ) : (
            <p className="text-sm text-gray-900">{checkDate || "-"}</p>
          )}
        </div>

        {/* 점검자/확인자 */}
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                점검자
              </label>
              {!readOnly ? (
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={inspector}
                  onChange={(e) => setInspector(e.target.value)}
                />
              ) : (
                <p className="text-sm text-gray-900">{inspector || "-"}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                확인자
              </label>
              {!readOnly ? (
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={confirmer}
                  onChange={(e) => setConfirmer(e.target.value)}
                />
              ) : (
                <p className="text-sm text-gray-900">{confirmer || "-"}</p>
              )}
            </div>
          </div>
        </div>

        {/* 문서 연결 */}
        {docType === "result" && !readOnly && (
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              문서 연결
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"
                onClick={() => setLinkModalOpen(true)}
              >
                예정 문서 연결
              </button>
              <span className="text-sm text-gray-600">
                {linkedDoc ? (
                  <>
                    연결됨: <b>{linkedDoc.title}</b>
                  </>
                ) : (
                  "-"
                )}
              </span>
            </div>
          </div>
        )}

        {/* ====================== 사진첨부 (주석 유지) ====================== */}
        {/*
          <tr>
            <TableCell
              label="사진 첨부"
              editing={!readOnly}
              value={
                photos.length
                  ? `${photos.length}개 선택됨`
                  : "선택된 사진이 없습니다."
              }
              input={
                <div>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={onPickPhotos}
                    className="input-base w-full"
                  />

                  {photos.length > 0 && (
                    <ul className="mt-2 text-xs text-gray-600 space-y-1">
                      {photos.map((p) => (
                        <li
                          key={p.id}
                          className="flex items-center justify-between"
                        >
                          <span>
                            📷 {p.name} ({Math.round(p.size / 1024)} KB)
                          </span>

                          {!readOnly && (
                            <button
                              type="button"
                              onClick={() =>
                                setPhotos((prev) =>
                                  prev.filter((x) => x.id !== p.id)
                                )
                              }
                              className="ml-2 text-rose-600 hover:underline"
                            >
                              ✕
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              }
            />
          </tr>
          */}

        {/* ====================== 메모(주석 유지) ====================== */}
        {/*
          <tr>
            <TableCell
              label="메모"
              editing={!readOnly}
              colSpan={3}
              value={memo}
              input={
                docType === "result" && linkedDoc ? (
                  <div className="text-gray-500 whitespace-pre-wrap">
                    {linkedDoc.values?.memo || "(연결된 문서의 메모 없음)"}
                  </div>
                ) : (
                  <textarea
                    className="textarea-base w-full min-h-[96px]"
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                  />
                )
              }
            />
          </tr>
          */}

        {/* 점검 사항 (TinyMCE) */}
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            점검 사항
          </label>
          {!readOnly ? (
            <div>
              <div className="mb-2 text-xs text-gray-500">
                {progressLabel} 항목을 입력하세요. 붙여넣기로 사진 첨부 가능.
              </div>
              <TinyMCEeditor
                initialValue={memo}
                editorRef={editorRef}
                onChange={(v) => setMemo(v)}
              />
            </div>
          ) : (
            <div
              className="text-sm text-gray-900 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: memo }}
            />
          )}
        </div>
      </section>

      {/* ====================== 조회 권한 옵션 (모바일 최적화) ====================== */}
      <div className="bg-white rounded-lg p-3 border border-gray-200 space-y-3">
        <div className="text-sm font-medium text-gray-900">조회 권한 옵션</div>

        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              className="flex-shrink-0"
              checked={viewMode === "ALL"}
              onChange={() => setViewMode("ALL")}
            />
            <span className="text-sm text-gray-700">
              전체 사용자가 조회 가능
            </span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="radio"
              className="flex-shrink-0"
              checked={viewMode === "REF"}
              onChange={() => setViewMode("REF")}
            />
            <span className="text-sm text-gray-700">
              일부 사용자만 조회 가능
            </span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="radio"
              className="flex-shrink-0"
              checked={viewMode === "ME"}
              onChange={() => setViewMode("ME")}
            />
            <span className="text-sm text-gray-700">나만 보기</span>
          </label>
        </div>

        {viewMode === "REF" && (
          <div className="pt-2 border-t border-gray-100">
            <button
              type="button"
              className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
              onClick={openReferenceModal}
            >
              조회 권한 설정
            </button>

            <div className="mt-2 text-xs text-gray-500">
              선택된 사용자에게 알림이 발송됩니다.
            </div>

            {referenceUserDetails.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {referenceUserDetails.map((u) => (
                  <span
                    key={u.id}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 border text-xs"
                  >
                    {u.last_name}
                    {u.first_name}
                    <span className="text-gray-500">
                      {u.position}/{u.company}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 조회권한 모달 */}
      {isMobile ? (
        <MobileReferenceModal
          isOpen={isReferenceModalOpen}
          onClose={closeReferenceModal}
          approvalLines={approvalLines}
          selectedIds={referenceUserIds}
          onChange={handleReferenceChange}
          onSave={handleReferenceSave}
          name="일정 조회 가능 사용자"
        />
      ) : (
        <ReferenceModal
          isOpen={isReferenceModalOpen}
          onClose={closeReferenceModal}
          approvalLines={approvalLines}
          selectedIds={referenceUserIds}
          onChange={handleReferenceChange}
          onSave={handleReferenceSave}
          name="일정 조회 가능 사용자"
        />
      )}
    </div>
  );
}
